import { useFormik } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { object, ref, string } from "yup";
import {
  dateSendToBackend,
  formatDateToMMDDYYYY,
  formateMobileNumber,
  validateNum,
} from "../../lib/constant";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import { register } from "../../services/AuthServices";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import authIllustrations from "../../assets/images/auth-illustrations.png";
import "../Login/Auth.css";

const MainSection = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onRecaptchaChange = (token) => setRecaptchaToken(token);
  const onRecaptchaExpired = () => setRecaptchaToken(null);
  const onRecaptchaError = () => setRecaptchaError(true);

  const resetRecaptcha = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const validationSchema = object().shape({
    email: string()
      .required("Please enter your email")
      .email("Please enter a valid email address")
      .trim(),
    password: string()
      .required("Please enter a password")
      .trim()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
        "Must contain 8+ chars, uppercase, lowercase and number"
      ),
    gender: string()
      .required("Please select your gender")
      .trim(),
    nationality: string()
      .required("Please enter your nationality")
      .trim(),
    confirmPassword: string()
      .required("Please confirm your password")
      .oneOf([ref("password")], "Passwords do not match"),
    firstName: string()
      .required("Please enter your first name")
      .trim(),
    lastName: string()
      .required("Please enter your last name")
      .trim(),
    phone: string()
      .required("Please enter your mobile number")
      .trim()
      .test("phone", "Mobile number must be exactly 10 digits", (value) => {
        return validateNum(value);
      }),
  });

  const submitRegister = async (data) => {
    setIsLoading(true);
    try {
      await register(data);
      setIsLoading(false);
      toast("Account created successfully!", { type: "success" });
      navigate("/login");
    } catch (error) {
      setIsLoading(false);
      resetRecaptcha();
      const message = error?.response?.data?.message || "Something went wrong";
      toast(message, { type: "error" });
    }
  };

  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    handleChange,
    values,
    errors,
    touched,
  } = useFormik({
    validationSchema,
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      nationality: "",
      gender: "",
      confirmPassword: "",
      phone: "",
      dateOfBirth: "",
    },
    onSubmit: async () => {
      if (!recaptchaError && !recaptchaToken) {
        return toast("Please complete the reCAPTCHA verification.", { type: "error" });
      }
      let formdata = new FormData();
      formdata.append("firstName", values.firstName);
      formdata.append("lastName", values.lastName);
      formdata.append("email", values.email);
      formdata.append("password", values.password);
      formdata.append("gender", values.gender);
      formdata.append("nationality", values.nationality);
      formdata.append("phone_number", values.phone);
      formdata.append("dateOfBirth", dateSendToBackend(values.dateOfBirth));
      submitRegister(formdata);
    },
  });

  const handleChangeMobile = (num) => {
    setFieldValue("phone", formateMobileNumber(num));
  };

  return (
    <div className="dp-auth-container" style={{ paddingTop: '100px' }}>
      <div className="dp-auth-card" style={{ maxWidth: '900px' }}>
        {/* Left Side - Illustration */}
        <div className="dp-auth-left">
          <div
            className="dp-auth-illustration-wrapper register"
            style={{ backgroundImage: `url(${authIllustrations})` }}
          ></div>
          <div className="dp-auth-brand">
            <h2>Join DreamPrize</h2>
            <p>Create your account and start winning amazing prizes today!</p>
            <div className="dp-auth-features">
              <div className="dp-auth-feature">
                <i className="fas fa-gift"></i>
                <span>Exclusive draws & prizes</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-users"></i>
                <span>Join thousands of winners</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-lock"></i>
                <span>100% secure registration</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Register Form */}
        <div className="dp-auth-right" style={{ padding: '40px' }}>
          <div className="dp-auth-header">
            <h1>Create Account</h1>
            <p>Fill in your details to get started</p>
          </div>

          <form className="dp-auth-form" onSubmit={handleSubmit}>
            {/* Name Row */}
            <div className="dp-form-row">
              <div className="dp-form-group">
                <label className="dp-form-label">First Name</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    className={`dp-form-input ${touched.firstName && errors.firstName ? 'error' : ''}`}
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                    style={{ paddingRight: '16px' }}
                  />
                </div>
                {touched.firstName && errors.firstName && (
                  <span className="dp-form-error">{errors.firstName}</span>
                )}
              </div>

              <div className="dp-form-group">
                <label className="dp-form-label">Last Name</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    className={`dp-form-input ${touched.lastName && errors.lastName ? 'error' : ''}`}
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                    style={{ paddingRight: '16px' }}
                  />
                </div>
                {touched.lastName && errors.lastName && (
                  <span className="dp-form-error">{errors.lastName}</span>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="dp-form-group">
              <label className="dp-form-label">Email Address</label>
              <div className="dp-form-input-wrapper">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className={`dp-form-input ${touched.email && errors.email ? 'error' : ''}`}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={100}
                />
                <i className="fal fa-envelope dp-form-icon"></i>
              </div>
              {touched.email && errors.email && (
                <span className="dp-form-error">{errors.email}</span>
              )}
            </div>

            {/* Password Row */}
            <div className="dp-form-row">
              <div className="dp-form-group">
                <label className="dp-form-label">Password</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Create password"
                    className={`dp-form-input ${touched.password && errors.password ? 'error' : ''}`}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                  />
                  <i
                    className={`fal ${showPassword ? 'fa-eye' : 'fa-eye-slash'} dp-form-icon clickable`}
                    onClick={() => setShowPassword(!showPassword)}
                  ></i>
                </div>
                {touched.password && errors.password && (
                  <span className="dp-form-error">{errors.password}</span>
                )}
              </div>

              <div className="dp-form-group">
                <label className="dp-form-label">Confirm Password</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm password"
                    className={`dp-form-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''}`}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                  />
                  <i
                    className={`fal ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'} dp-form-icon clickable`}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  ></i>
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <span className="dp-form-error">{errors.confirmPassword}</span>
                )}
              </div>
            </div>

            {/* Phone & Nationality Row */}
            <div className="dp-form-row">
              <div className="dp-form-group">
                <label className="dp-form-label">Mobile Number</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type="text"
                    name="phone"
                    placeholder="Enter mobile number"
                    className={`dp-form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={14}
                  />
                  <i className="fal fa-phone dp-form-icon"></i>
                </div>
                {touched.phone && errors.phone && (
                  <span className="dp-form-error">{errors.phone}</span>
                )}
              </div>

              <div className="dp-form-group">
                <label className="dp-form-label">Nationality</label>
                <div className="dp-form-input-wrapper">
                  <input
                    type="text"
                    name="nationality"
                    placeholder="Enter nationality"
                    className={`dp-form-input ${touched.nationality && errors.nationality ? 'error' : ''}`}
                    value={values.nationality}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                  />
                  <i className="fal fa-globe dp-form-icon"></i>
                </div>
                {touched.nationality && errors.nationality && (
                  <span className="dp-form-error">{errors.nationality}</span>
                )}
              </div>
            </div>

            {/* Gender & DOB Row */}
            <div className="dp-form-row">
              <div className="dp-form-group">
                <label className="dp-form-label">Gender</label>
                <select
                  name="gender"
                  className={`dp-form-select ${touched.gender && errors.gender ? 'error' : ''}`}
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="others">Others</option>
                </select>
                {touched.gender && errors.gender && (
                  <span className="dp-form-error">{errors.gender}</span>
                )}
              </div>

              <div className="dp-form-group">
                <label className="dp-form-label">Date of Birth</label>
                <ReactDatePicker
                  placeholderText="Select date of birth"
                  value={values.dateOfBirth}
                  autoComplete="off"
                  maxDate={new Date()}
                  onChange={(e) => {
                    setFieldValue("dateOfBirth", formatDateToMMDDYYYY(new Date(e)));
                  }}
                  name="dateOfBirth"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  showMonthDropdown
                  className="dp-form-input"
                  wrapperClassName="dp-datepicker-wrapper"
                />
                {touched.dateOfBirth && errors.dateOfBirth && (
                  <span className="dp-form-error">{errors.dateOfBirth}</span>
                )}
              </div>
            </div>

            {/* reCAPTCHA */}
            {!recaptchaError && (
              <div className="dp-recaptcha-wrapper">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchaChange}
                  onExpired={onRecaptchaExpired}
                  onErrored={onRecaptchaError}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="dp-auth-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Creating Account...
                </>
              ) : (
                <>
                  <i className="far fa-user-plus"></i>
                  Create Account
                </>
              )}
            </button>

            {/* Footer */}
            <div className="dp-auth-footer">
              <p>
                Already have an account?{" "}
                <span onClick={() => navigate("/login")}>Login</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MainSection;
